import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FlagsmithProvider, useFlagsmith, useFlagsmithLoading } from '@flagsmith/flagsmith/react';
import type { RootState } from 'store/store';
import { SHOW_SNACKBAR } from 'store/snackbar/snackbar.action';
import { FeatureFlagsReadyContext } from './featureFlagsContext';
import {
  buildInitConfig,
  flagsmithClient,
  getFlagsmithEnvironmentId,
  FLAG_FETCH_TIMEOUT_MS,
  FLAGS_UNAVAILABLE_MESSAGE,
} from './flagsmithClient';
import type { FlagsmithClient } from './flagsmithClient';

type FeatureFlagsProviderProps = {
  children: React.ReactNode;
  /** Swap in a throwaway Flagsmith instance. For tests only - the app uses the shared one. */
  client?: FlagsmithClient;
};

/**
 * Makes feature flags available to the app, and keeps Flagsmith pointed at whoever is
 * signed in. Mount it once, above anything that reads a flag.
 *
 * The guiding rule here is fail open: Flagsmith being slow, misconfigured or down must
 * never stop the app from rendering. When that happens we warn the user once and treat
 * every flag as off.
 */
export default function FeatureFlagsProvider({ children, client = flagsmithClient }: FeatureFlagsProviderProps) {
  return (
    // No `options` prop on purpose: passing one would make Flagsmith initialise itself
    // immediately, before we know who the user is. FlagsmithIdentitySync does it instead.
    <FlagsmithProvider flagsmith={client}>
      <FlagsmithIdentitySync>{children}</FlagsmithIdentitySync>
    </FlagsmithProvider>
  );
}

/**
 * Loads the signed-in user's flags, and reloads them when the user changes.
 */
function FlagsmithIdentitySync({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  const flagsmith = useFlagsmith();
  // `isLoading` is false as soon as there are flags to read, whatever their source. We
  // deliberately ignore its sibling `isFetching`, which is also true during background
  // refreshes - gating on that would drop the app back to "not ready" long after startup.
  const { isLoading } = useFlagsmithLoading() ?? {};
  const auth = useSelector((state: RootState) => state.auth);

  /** The user whose flags we have finished loading (or given up on). */
  const [emailWithLoadedFlags, setEmailWithLoadedFlags] = useState<string | null>(null);
  const [isUsingFallbackFlags, setIsUsingFallbackFlags] = useState(false);
  const hasWarnedRef = useRef(false);

  const isSignedIn = Boolean(auth?.authenticated);
  // The email is the Flagsmith identity. Normalise it, because Flagsmith identities are
  // case sensitive: `User@flybits.com` and `user@flybits.com` would otherwise be two
  // separate identities with two separate sets of flags.
  const signedInEmail = isSignedIn ? auth?.user?.email?.trim().toLowerCase() || null : null;

  /** Once per session is enough - a flag outage repeats, and the user can only act on it once. */
  const warnFlagsUnavailableOnce = useCallback(() => {
    if (hasWarnedRef.current) return;
    hasWarnedRef.current = true;
    dispatch({
      type: SHOW_SNACKBAR,
      payload: { content: FLAGS_UNAVAILABLE_MESSAGE, type: 'error' },
    });
  }, [dispatch]);

  /** Give up on Flagsmith and let the app through with every flag off. */
  const fallBackToFlagsOff = useCallback(
    (email: string | null) => {
      warnFlagsUnavailableOnce();
      setEmailWithLoadedFlags(email);
      setIsUsingFallbackFlags(true);
    },
    [warnFlagsUnavailableOnce],
  );

  useEffect(() => {
    if (!isSignedIn) {
      setEmailWithLoadedFlags(null);
      setIsUsingFallbackFlags(false);
      // Forget the previous user's flags so they cannot leak into the next session. This
      // costs one request for the environment's anonymous flags, which is an acceptable
      // price on sign-out.
      if (flagsmith.initialised && flagsmith.identity) {
        flagsmith.logout().catch(() => undefined);
      }
      return undefined;
    }

    // Signed in with no email: there is nothing to identify with, so nothing to wait for.
    if (!signedInEmail) {
      fallBackToFlagsOff(null);
      return undefined;
    }

    const environmentID = getFlagsmithEnvironmentId();
    if (!environmentID) {
      fallBackToFlagsOff(signedInEmail);
      return undefined;
    }

    // We already hold this user's flags. Happens when the effect re-runs for an unrelated
    // reason, including React mounting twice in StrictMode - don't fetch again.
    if (flagsmith.initialised && flagsmith.identity === signedInEmail) {
      setEmailWithLoadedFlags(signedInEmail);
      setIsUsingFallbackFlags(false);
      return undefined;
    }

    let isCurrentRequest = true;
    setIsUsingFallbackFlags(false);

    // Flagsmith has no request timeout of its own, so without this a request that never
    // comes back would leave anything behind `useFeatureFlagsReady()` waiting forever.
    const timeoutId = window.setTimeout(() => {
      if (!isCurrentRequest) return;
      isCurrentRequest = false;
      fallBackToFlagsOff(signedInEmail);
    }, FLAG_FETCH_TIMEOUT_MS);

    // `init` is the one-off setup call and fetches the first flags; after that, changing
    // user is an `identify`. Calling `init` twice on one instance is not supported.
    const flagsLoaded = flagsmith.initialised
      ? flagsmith.identify(signedInEmail)
      : flagsmith.init(buildInitConfig(environmentID, signedInEmail));

    flagsLoaded
      .then(() => {
        if (!isCurrentRequest) return;
        isCurrentRequest = false;
        window.clearTimeout(timeoutId);
        setEmailWithLoadedFlags(signedInEmail);
        setIsUsingFallbackFlags(false);
      })
      .catch(() => {
        if (!isCurrentRequest) return;
        isCurrentRequest = false;
        window.clearTimeout(timeoutId);
        fallBackToFlagsOff(signedInEmail);
      });

    // If the user changed while the request was in flight, its answer is for the previous
    // user, so ignore it rather than marking the new user's flags as loaded.
    return () => {
      isCurrentRequest = false;
      window.clearTimeout(timeoutId);
    };
  }, [isSignedIn, signedInEmail, flagsmith, fallBackToFlagsOff]);

  const areFlagsReady = isSignedIn && signedInEmail === emailWithLoadedFlags && (isUsingFallbackFlags || !isLoading);

  return <FeatureFlagsReadyContext value={areFlagsReady}>{children}</FeatureFlagsReadyContext>;
}
